import { useAuth } from "@clerk/react";
import {
  AlertCircleIcon,
  BlocksIcon,
  CheckCircle2Icon,
  CloudIcon,
  ExternalLinkIcon,
  KeyRoundIcon,
  LoaderIcon,
  PlayIcon,
  RefreshCwIcon,
  Settings2Icon,
  SquareIcon,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";

import type { CloudEnvironmentControlResult, EnvironmentId } from "@t3tools/contracts";
import { hasCloudPublicConfig, hasFabricaAccountPublicConfig } from "../../cloud/publicConfig";
import type { EnvironmentPresentation } from "../../state/environments";
import { ensureLocalApi } from "../../localApi";
import { useT3ConnectAuthPrompt } from "../clerk/useT3ConnectAuthPrompt";
import { Alert, AlertDescription, AlertTitle } from "../ui/alert";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { stackedThreadToast, toastManager } from "../ui/toast";
import { SettingsSection } from "./settingsLayout";
import { searchableSetting } from "./settingsSearch";
import {
  cloudProviderSetupLabel,
  presentCloudSetupState,
  resolveCloudSetupOperation,
  CLOUD_CONNECT_RECONCILIATION_DELAYS_MS,
  type FabricaAccountState,
  type CloudSetupAction,
} from "./CloudSetupPanel.logic";
import {
  presentCloudOnboarding,
  resolveCloudOnboardingResume,
  resolveCloudPanelBody,
} from "./CloudOnboarding.logic";
import { CloudOnboardingSteps, type CloudOnboardingStepActions } from "./CloudOnboardingSteps";

type AsyncAction = () => Promise<void> | void;
type CloudControlAction = () => Promise<CloudEnvironmentControlResult>;
type CompleteCloudAuthorizationAction = (
  authorizationCode: string,
) => Promise<CloudEnvironmentControlResult>;

export interface CloudSetupPanelProps {
  /** The environment selected in the provider settings device picker. */
  readonly selectedEnvironment: EnvironmentPresentation | null;
  /** Number of environments whose descriptor explicitly advertises cloud placement. */
  readonly cloudEnvironmentCount: number;
  /** Opens the existing Connections surface for Cloud setup/pairing. */
  readonly onOpenConnections?: AsyncAction;
  /** Opens the Providers surface where a Cloud subscription is connected. */
  readonly onOpenProviders?: AsyncAction;
  /** Retries a selected Cloud transport after its runtime is already active. */
  readonly onRetryEnvironment?: AsyncAction;
  /** Starts or resumes the AgentCore runtime through Fabrica's managed control service. */
  readonly onProvisionCloud?: CloudControlAction;
  /** Completes the PKCE-bound headless Fabrica Connect authorization. */
  readonly onCompleteCloudAuthorization?: CompleteCloudAuthorizationAction;
  /** Stops the selected Cloud runtime through the local host server. */
  readonly onStopCloud?: CloudControlAction;
  /** Refreshes relay discovery after the runtime links itself. */
  readonly onRefreshCloudConnections?: AsyncAction;
  /** Whether the environment catalog has completed its initial hydration. */
  readonly catalogReady?: boolean;
  /** Clerk account state used to gate every operation that can start Cloud compute. */
  readonly accountState: FabricaAccountState;
  /** Whether the public relay URL and Clerk JWT template are ready. */
  readonly relayReady: boolean;
  /** Opens the Fabrica account sign-in UI when a provisioning action is signed out. */
  readonly onSignIn?: AsyncAction;
}

type FabricaCloudSetupPanelProps = Omit<
  CloudSetupPanelProps,
  "accountState" | "relayReady" | "onSignIn"
>;

interface CloudSetupOperationHandlers {
  readonly onSignIn?: AsyncAction;
  readonly onProvisionCloud?: CloudControlAction;
  readonly onRetryEnvironment?: AsyncAction;
  readonly onOpenConnections?: AsyncAction;
}

/** The primary button uses this dispatcher after resolving its fail-closed operation. */
export function runCloudSetupOperation(
  operation: ReturnType<typeof resolveCloudSetupOperation>,
  handlers: CloudSetupOperationHandlers,
): Promise<CloudEnvironmentControlResult | void> | void {
  switch (operation) {
    case "sign-in":
      return handlers.onSignIn?.();
    case "provision":
      return handlers.onProvisionCloud?.();
    case "retry":
      return handlers.onRetryEnvironment?.();
    case "open-connections":
      return handlers.onOpenConnections?.();
    case null:
      return undefined;
  }
}

interface CloudControlSnapshot {
  readonly environmentId: EnvironmentId | null;
  readonly result: CloudEnvironmentControlResult;
}

function statusIcon(state: ReturnType<typeof presentCloudSetupState>["state"]) {
  switch (state) {
    case "ready":
      return CheckCircle2Icon;
    case "starting":
    case "resuming":
      return LoaderIcon;
    case "error":
      return AlertCircleIcon;
    default:
      return CloudIcon;
  }
}

function statusBadgeVariant(
  tone: ReturnType<typeof presentCloudSetupState>["tone"],
): "outline" | "success" | "warning" | "error" {
  if (tone === "success") return "success";
  if (tone === "warning") return "warning";
  if (tone === "error") return "error";
  return "outline";
}

function actionIcon(action: CloudSetupAction) {
  switch (action) {
    case "start":
      return PlayIcon;
    case "resume":
    case "retry":
      return RefreshCwIcon;
    case "setup":
      return Settings2Icon;
    default:
      return null;
  }
}

function signInActionLabel(action: CloudSetupAction): string {
  switch (action) {
    case "start":
      return "Sign in to start Cloud";
    case "resume":
      return "Sign in to resume Cloud";
    case "retry":
      return "Sign in to retry Cloud";
    default:
      return "Sign in to set up Cloud";
  }
}

export function CloudSetupPanel({
  selectedEnvironment,
  cloudEnvironmentCount,
  onOpenConnections,
  onOpenProviders,
  onRetryEnvironment,
  onProvisionCloud,
  onCompleteCloudAuthorization,
  onStopCloud,
  onRefreshCloudConnections,
  catalogReady = true,
  accountState,
  relayReady,
  onSignIn,
}: CloudSetupPanelProps) {
  const [actionState, setActionState] = useState<
    | { readonly status: "idle" }
    | { readonly status: "running" }
    | { readonly status: "error"; readonly message: string }
  >({ status: "idle" });
  const [stopState, setStopState] = useState<"idle" | "confirming" | "running">("idle");
  const [cloudControlSnapshot, setCloudControlSnapshot] = useState<CloudControlSnapshot | null>(
    null,
  );
  const [connectingReconciliation, setConnectingReconciliation] = useState<"pending" | "exhausted">(
    "pending",
  );
  const [authorizationCode, setAuthorizationCode] = useState("");
  // A setup click made while signed out is remembered so it can continue once
  // Clerk reports the session, instead of asking for a second click.
  const [pendingSetup, setPendingSetup] = useState(false);
  const selectedEnvironmentId = selectedEnvironment?.environmentId ?? null;
  const selectedEnvironmentIsCloud =
    selectedEnvironment?.serverConfig?.environment.placement === "cloud";
  const cloudControlResult =
    cloudControlSnapshot?.environmentId === selectedEnvironmentId
      ? cloudControlSnapshot.result
      : null;
  const presentation = presentCloudSetupState({
    catalogReady,
    selectedEnvironmentIsCloud,
    cloudEnvironmentCount,
    connectionPhase: selectedEnvironmentIsCloud
      ? (selectedEnvironment?.connection.phase ?? null)
      : null,
    ...(cloudControlResult === null ? {} : { runtimeControlState: cloudControlResult.state }),
    ...(cloudControlResult?.state === "connecting"
      ? { runtimeControlReconciliation: connectingReconciliation }
      : {}),
  });
  const StatusIcon = statusIcon(presentation.state);
  const ActionIcon = actionIcon(presentation.action);
  const actionBusy = actionState.status === "running" || stopState !== "idle";
  const operation = resolveCloudSetupOperation({
    selectedEnvironmentIsCloud,
    action: presentation.action,
    accountState,
    relayReady,
    hasSignIn: onSignIn !== undefined,
    hasProvisionCloud: onProvisionCloud !== undefined,
    hasRetryEnvironment: onRetryEnvironment !== undefined,
    hasOpenConnections: onOpenConnections !== undefined,
  });
  const actionHandlers: CloudSetupOperationHandlers = {
    ...(onSignIn ? { onSignIn } : {}),
    ...(onProvisionCloud ? { onProvisionCloud } : {}),
    ...(onRetryEnvironment ? { onRetryEnvironment } : {}),
    ...(onOpenConnections ? { onOpenConnections } : {}),
  };
  const hasActionHandler =
    (operation === "sign-in" && onSignIn !== undefined) ||
    (operation === "provision" && onProvisionCloud !== undefined) ||
    (operation === "retry" && onRetryEnvironment !== undefined) ||
    (operation === "open-connections" && onOpenConnections !== undefined);
  const provisioningAction = operation === "provision";
  const signingInAction = operation === "sign-in";
  const canAct = hasActionHandler && !actionBusy && presentation.action !== null;
  const canStopCloud =
    onStopCloud !== undefined &&
    (!selectedEnvironmentIsCloud
      ? cloudControlResult?.state === "ready" ||
        cloudControlResult?.state === "connecting" ||
        cloudControlResult?.state === "needs_connect_authorization"
      : selectedEnvironment?.connection.phase === "connected" ||
        selectedEnvironment?.connection.phase === "connecting" ||
        selectedEnvironment?.connection.phase === "reconnecting" ||
        selectedEnvironment?.connection.phase === "error");

  useEffect(() => {
    if (
      cloudControlSnapshot !== null &&
      cloudControlSnapshot.environmentId !== selectedEnvironmentId
    ) {
      setCloudControlSnapshot(null);
    }
  }, [cloudControlSnapshot, selectedEnvironmentId]);

  useEffect(() => {
    if (
      cloudEnvironmentCount > 0 &&
      (cloudControlSnapshot?.result.state === "connecting" ||
        cloudControlSnapshot?.result.state === "ready")
    ) {
      setCloudControlSnapshot(null);
    }
  }, [cloudControlSnapshot, cloudEnvironmentCount]);

  useEffect(() => {
    if (cloudControlSnapshot?.result.state !== "connecting") {
      setConnectingReconciliation("pending");
      return;
    }

    if (onRefreshCloudConnections === undefined) {
      setConnectingReconciliation("exhausted");
      return;
    }

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    setConnectingReconciliation("pending");

    const reconcile = async () => {
      for (const delayMs of CLOUD_CONNECT_RECONCILIATION_DELAYS_MS) {
        await new Promise<void>((resolve) => {
          timer = setTimeout(resolve, delayMs);
        });
        if (cancelled) return;
        try {
          await onRefreshCloudConnections();
        } catch {
          // A failed discovery attempt is folded into the finite retry budget.
          // The visible state below gives the user an explicit retry action.
        }
      }
      if (!cancelled) {
        setConnectingReconciliation("exhausted");
      }
    };

    void reconcile();
    return () => {
      cancelled = true;
      if (timer !== null) clearTimeout(timer);
    };
  }, [cloudControlSnapshot, onRefreshCloudConnections]);

  const runAction = async () => {
    if (!hasActionHandler || actionBusy) return;
    if (operation === "sign-in") setPendingSetup(true);
    setActionState({ status: "running" });
    try {
      const result = await runCloudSetupOperation(operation, actionHandlers);
      if (result && typeof result === "object" && "state" in result) {
        if (result.state === "connecting") {
          setConnectingReconciliation("pending");
        }
        setCloudControlSnapshot({ environmentId: selectedEnvironmentId, result });
        if (result.state === "ready" || result.state === "connecting") {
          await onRefreshCloudConnections?.();
        }
      }
      setActionState({ status: "idle" });
    } catch (error) {
      setPendingSetup(false);
      const message = error instanceof Error ? error.message : "Could not update Cloud status.";
      setActionState({ status: "error", message });
      toastManager.add(
        stackedThreadToast({
          type: "error",
          title: "Could not update Cloud",
          description: message,
        }),
      );
    }
  };

  const runActionRef = useRef(runAction);
  useEffect(() => {
    runActionRef.current = runAction;
  });

  const resumeAfterSignIn = resolveCloudOnboardingResume({
    pending: pendingSetup,
    accountState,
    relayReady,
  });
  useEffect(() => {
    if (resumeAfterSignIn === "hold") return;
    setPendingSetup(false);
    if (resumeAfterSignIn === "run") {
      void runActionRef.current();
    }
  }, [resumeAfterSignIn]);

  const openAuthorization = () => {
    if (cloudControlResult?.state !== "needs_connect_authorization") return;
    window.open(cloudControlResult.authorizeUrl, "_blank", "noopener,noreferrer");
  };

  const completeAuthorization = async () => {
    const code = authorizationCode.trim();
    if (!code || !onCompleteCloudAuthorization || actionBusy) return;
    setActionState({ status: "running" });
    try {
      const result = await onCompleteCloudAuthorization(code);
      if (result.state === "connecting") {
        setConnectingReconciliation("pending");
      }
      setCloudControlSnapshot({ environmentId: selectedEnvironmentId, result });
      setAuthorizationCode("");
      if (result.state === "ready" || result.state === "connecting") {
        await onRefreshCloudConnections?.();
      }
      setActionState({ status: "idle" });
    } catch (error) {
      const message = error instanceof Error ? error.message : "Could not authorize Cloud.";
      setActionState({ status: "error", message });
      toastManager.add(
        stackedThreadToast({
          type: "error",
          title: "Could not authorize Cloud",
          description: message,
        }),
      );
    }
  };

  const stopCloud = async () => {
    if (!onStopCloud || !canStopCloud || stopState !== "idle") return;
    setStopState("confirming");
    try {
      const confirmed = await ensureLocalApi().dialogs.confirm(
        [
          "Stop the Cloud runtime?",
          "Running Cloud work will be interrupted and the runtime will be stopped.",
          "You can start it again later. Local execution is unaffected.",
        ].join("\n"),
        { variant: "destructive" },
      );
      if (!confirmed) return;

      setStopState("running");
      setActionState({ status: "running" });
      const result = await onStopCloud();
      setCloudControlSnapshot({ environmentId: selectedEnvironmentId, result });
      await onRefreshCloudConnections?.();
      setActionState({ status: "idle" });
    } catch (error) {
      const message = error instanceof Error ? error.message : "Could not stop Cloud.";
      setActionState({ status: "error", message });
      toastManager.add(
        stackedThreadToast({
          type: "error",
          title: "Could not stop Cloud",
          description: message,
        }),
      );
    } finally {
      setStopState("idle");
    }
  };

  const providerLabel = cloudProviderSetupLabel({
    selectedEnvironmentIsCloud,
    cloudSetupState: presentation.state,
  });

  const panelBody = resolveCloudPanelBody({
    cloudEnvironmentCount,
    selectedEnvironmentIsCloud,
    cloudSetupState: presentation.state,
  });
  const onboardingSteps = presentCloudOnboarding({
    accountState,
    relayReady,
    cloudSetupState: presentation.state,
  });

  const primaryActionButton =
    presentation.action && ActionIcon ? (
      <Button
        size="xs"
        variant={presentation.action === "retry" ? "outline" : "default"}
        disabled={!canAct}
        aria-busy={actionBusy}
        onClick={() => void runAction()}
      >
        {actionBusy ? (
          <LoaderIcon className="size-3 animate-spin" aria-hidden />
        ) : (
          <ActionIcon className="size-3" aria-hidden />
        )}
        {actionBusy
          ? signingInAction
            ? "Opening sign-in…"
            : provisioningAction
              ? "Starting Cloud…"
              : presentation.action === "setup"
                ? "Opening…"
                : "Connecting…"
          : signingInAction
            ? signInActionLabel(presentation.action)
            : presentation.actionLabel}
      </Button>
    ) : null;

  const authorizationControls =
    cloudControlResult?.state === "needs_connect_authorization" ? (
      <div className="space-y-2">
        <Button size="xs" variant="outline" onClick={openAuthorization}>
          <ExternalLinkIcon className="size-3" aria-hidden />
          Open authorization
        </Button>
        <div className="flex flex-col gap-2 sm:flex-row">
          <label className="sr-only" htmlFor="cloud-connect-authorization-code">
            One-time Connect authorization code
          </label>
          <Input
            id="cloud-connect-authorization-code"
            nativeInput
            autoComplete="off"
            spellCheck={false}
            value={authorizationCode}
            onChange={(event) => setAuthorizationCode(event.currentTarget.value)}
            placeholder="Paste the one-time Connect code"
            className="flex-1 font-mono"
            disabled={actionBusy}
          />
          <Button
            size="sm"
            disabled={
              actionBusy ||
              authorizationCode.trim().length === 0 ||
              onCompleteCloudAuthorization === undefined
            }
            onClick={() => void completeAuthorization()}
          >
            {actionBusy ? (
              <LoaderIcon className="size-3 animate-spin" aria-hidden />
            ) : (
              <KeyRoundIcon className="size-3" aria-hidden />
            )}
            Finish connection
          </Button>
        </div>
      </div>
    ) : null;

  // The primary button drives whichever step is current: it opens Fabrica
  // sign-in while signed out, and provisions the runtime once signed in.
  const onboardingActions: CloudOnboardingStepActions =
    accountState === "signed-in"
      ? {
          runtime: primaryActionButton,
          ...(authorizationControls ? { authorize: authorizationControls } : {}),
        }
      : { account: primaryActionButton };

  return (
    <SettingsSection
      {...searchableSetting("cloud-setup")}
      icon={<CloudIcon className="size-4 text-muted-foreground" aria-hidden />}
      data-testid="cloud-setup-panel"
      headerAction={
        <Badge variant="warning" size="sm">
          Preview
        </Badge>
      }
    >
      <div className="overflow-hidden rounded-xl border border-border/70 bg-background/35">
        <div className="flex flex-col gap-3 border-b border-border/60 px-3 py-3 sm:flex-row sm:items-start sm:justify-between sm:px-4">
          <div className="flex min-w-0 items-start gap-3">
            <span className="flex size-9 shrink-0 items-center justify-center rounded-lg border border-primary/20 bg-primary/8 text-primary">
              <StatusIcon className={actionBusy ? "size-4 animate-spin" : "size-4"} aria-hidden />
            </span>
            <div className="min-w-0">
              <h3 className="text-sm font-medium text-foreground">Cloud runtime</h3>
              <p className="mt-1 max-w-xl text-[13px] leading-[1.45] text-muted-foreground/80">
                {presentation.description}
              </p>
            </div>
          </div>
          <Badge variant={statusBadgeVariant(presentation.tone)} size="sm">
            {presentation.label}
          </Badge>
        </div>

        {panelBody === "loading" ? null : panelBody === "onboarding" ? (
          <CloudOnboardingSteps steps={onboardingSteps} actions={onboardingActions} />
        ) : (
          <div className="divide-y divide-border/60">
            <div className="flex flex-col gap-3 px-3 py-3 sm:flex-row sm:items-center sm:justify-between sm:px-4">
              <div className="min-w-0">
                <p className="text-sm font-medium text-foreground">Execution target</p>
                <p className="mt-1 text-[13px] leading-[1.45] text-muted-foreground/80">
                  {selectedEnvironmentIsCloud
                    ? `${selectedEnvironment?.label ?? "Fabrica Cloud"} · isolated AgentCore runtime`
                    : "Local is selected. Choose a Cloud environment when you want remote execution."}
                </p>
              </div>
              <div className="flex flex-wrap items-center justify-end gap-2">
                {primaryActionButton}
                {canStopCloud ? (
                  <Button
                    size="xs"
                    variant="destructive-outline"
                    disabled={actionBusy}
                    aria-busy={stopState !== "idle"}
                    onClick={() => void stopCloud()}
                  >
                    {stopState === "confirming" || stopState === "running" ? (
                      <LoaderIcon className="size-3 animate-spin" aria-hidden />
                    ) : (
                      <SquareIcon className="size-3" aria-hidden />
                    )}
                    {stopState === "confirming"
                      ? "Confirming…"
                      : stopState === "running"
                        ? "Stopping…"
                        : "Stop Cloud"}
                  </Button>
                ) : null}
              </div>
            </div>

            <div className="flex flex-col gap-3 px-3 py-3 sm:flex-row sm:items-center sm:justify-between sm:px-4">
              <div className="min-w-0">
                <p className="text-sm font-medium text-foreground">Fabrica account</p>
                <p className="mt-1 text-[13px] leading-[1.45] text-muted-foreground/80">
                  {accountState === "signed-in"
                    ? "Signed in. New Cloud environments will be connected to this account."
                    : accountState === "signed-out"
                      ? "Sign in once before creating your first Cloud environment."
                      : accountState === "loading"
                        ? "Checking your Fabrica session."
                        : "Fabrica account sign-in is not configured for this build yet."}
                </p>
              </div>
              {accountState === "signed-out" && onSignIn ? (
                <Button
                  size="xs"
                  variant="outline"
                  disabled={actionBusy}
                  onClick={() => void onSignIn()}
                >
                  <KeyRoundIcon className="size-3" aria-hidden />
                  Sign in to Fabrica
                </Button>
              ) : accountState === "signed-in" ? (
                <Badge variant="success" size="sm">
                  Signed in
                </Badge>
              ) : accountState === "loading" ? (
                <Badge variant="outline" size="sm">
                  Checking…
                </Badge>
              ) : (
                <Badge variant="warning" size="sm">
                  Configuration required
                </Badge>
              )}
            </div>

            <div className="flex flex-col gap-3 px-3 py-3 sm:flex-row sm:items-center sm:justify-between sm:px-4">
              <div className="min-w-0">
                <p className="text-sm font-medium text-foreground">Fabrica Connect</p>
                <p className="mt-1 text-[13px] leading-[1.45] text-muted-foreground/80">
                  {relayReady
                    ? "Ready to connect this Cloud runtime to your Fabrica account."
                    : "Cloud compute will stay off until the relay URL and account token template are configured."}
                </p>
              </div>
              <Badge variant={relayReady ? "success" : "warning"} size="sm">
                {relayReady ? "Ready" : "Configuration required"}
              </Badge>
            </div>

            <div className="space-y-2 px-3 py-3 sm:px-4">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-foreground">Provider subscriptions</p>
                  <p className="mt-1 text-[13px] leading-[1.45] text-muted-foreground/80">
                    Subscriptions are connected per environment and stay inside the Cloud
                    environment.
                  </p>
                </div>
                {onOpenProviders ? (
                  <Button size="xs" variant="outline" onClick={() => void onOpenProviders()}>
                    <BlocksIcon className="size-3" aria-hidden />
                    Open Providers
                  </Button>
                ) : null}
              </div>
              <div className="grid gap-1 sm:grid-cols-2">
                <div className="rounded-lg border border-border/60 bg-background/30 px-3 py-2.5">
                  <p className="text-xs font-medium text-foreground">Codex subscription</p>
                  <p className="mt-1 text-xs text-muted-foreground">{providerLabel}</p>
                </div>
                <div className="rounded-lg border border-border/60 bg-background/30 px-3 py-2.5">
                  <p className="text-xs font-medium text-foreground">Claude Code subscription</p>
                  <p className="mt-1 text-xs text-muted-foreground">{providerLabel}</p>
                </div>
              </div>
            </div>

            {authorizationControls ? (
              <div className="space-y-3 px-3 py-3 sm:px-4">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-foreground">Authorize this runtime once</p>
                  <p className="mt-1 text-[13px] leading-[1.45] text-muted-foreground/80">
                    Authorize in the browser, then paste the one-time Connect code below.
                  </p>
                </div>
                {authorizationControls}
              </div>
            ) : null}
          </div>
        )}

        <div className="flex items-start gap-2 border-t border-border/60 bg-muted/18 px-3 py-3 text-xs text-muted-foreground sm:px-4">
          <CloudIcon className="mt-0.5 size-3.5 shrink-0" aria-hidden />
          <p>
            <span className="font-medium text-foreground/85">Local stays free.</span> AgentCore
            compute runs only while the Cloud runtime is active; relay, storage, database, and
            provider usage are billed separately.
          </p>
        </div>

        {actionState.status === "error" ? (
          <div className="border-t border-border/60 px-3 py-3 sm:px-4">
            <Alert variant="error" controlAlignment="first-line">
              <AlertCircleIcon aria-hidden />
              <AlertTitle>Cloud setup did not change</AlertTitle>
              <AlertDescription>{actionState.message}</AlertDescription>
            </Alert>
          </div>
        ) : null}
      </div>
    </SettingsSection>
  );
}

/**
 * Adds the optional Clerk account boundary without ever calling Clerk hooks in
 * builds that have no publishable key (and therefore no ClerkProvider).
 */
export function FabricaCloudSetupPanel(props: FabricaCloudSetupPanelProps) {
  if (!hasFabricaAccountPublicConfig()) {
    return <CloudSetupPanel {...props} accountState="unconfigured" relayReady={false} />;
  }

  return <ConfiguredFabricaCloudSetupPanel {...props} />;
}

function ConfiguredFabricaCloudSetupPanel(props: FabricaCloudSetupPanelProps) {
  const { isLoaded, isSignedIn } = useAuth({ treatPendingAsSignedOut: false });
  const { authPrompt, openAuthPrompt } = useT3ConnectAuthPrompt();
  const relayReady = hasCloudPublicConfig();
  const accountState: FabricaAccountState = !isLoaded
    ? "loading"
    : isSignedIn
      ? "signed-in"
      : "signed-out";

  return (
    <>
      <CloudSetupPanel
        {...props}
        accountState={accountState}
        relayReady={relayReady}
        onSignIn={openAuthPrompt}
      />
      {authPrompt}
    </>
  );
}
